import env from '@beam-australia/react-env';
import React from 'react';
import PropTypes from 'prop-types';
import { GA_DEBUG_MODE } from '@/lib/constants/localStorageKeys';
import useCurrentOrg from '@/lib/hooks/swr/useCurrentOrg';

const GA_MEASUREMENT_ID = env('GA_MEASUREMENT_ID');
const isProduction = env('NODE_ENV') === 'production';
const hasDebugMode =
  localStorage.getItem(GA_DEBUG_MODE) === 'true' && !isProduction;

export const GoogleAnalyticsContext = React.createContext();

export function useGoogleAnalytics() {
  return React.useContext(GoogleAnalyticsContext);
}

export default function GoogleAnalyticsProvider({ children }) {
  const { data: { isDemoOrg } = {} } = useCurrentOrg();

  // Because useCallback does not always update the latest state values,
  // refs will be used for tracking pertinent values internally.
  const isDemoOrgRef = React.useRef(isDemoOrg);

  // When logging in, a number of calls to gtag can be made before the
  // org data has finished fetching. To deal with this, we queue any
  // requests to google analytics. Once we have the org data, the queue
  // is emptied.
  const gTagQueueRef = React.useRef([]);

  const callGTag = React.useCallback((...args) => {
    if (typeof isDemoOrgRef.current === 'undefined') {
      gTagQueueRef.current.push(args);

      return;
    }

    // If it is a demo org, do not track anything.
    if (isDemoOrgRef.current) {
      return;
    }

    window.gtag(...args);
  }, []);

  React.useEffect(() => {
    isDemoOrgRef.current = isDemoOrg;

    if (typeof isDemoOrgRef.current === 'undefined') {
      return;
    }

    // When we have a proper "is demo org" value, check the queue to see
    // if there are any pending analytics that need to be tracked.
    if (gTagQueueRef.current.length) {
      // Only track those that do not belong to demo orgs.
      if (!isDemoOrgRef.current) {
        gTagQueueRef.current.forEach((gTagArgs) => callGTag(...gTagArgs));
      }

      gTagQueueRef.current = [];
    }
  }, [isDemoOrg]);

  const sendEvent = React.useCallback((eventName, params) => {
    if (
      params !== null &&
      typeof params === 'object' &&
      !Array.isArray(params)
    ) {
      callGTag('event', eventName, params);
    } else {
      callGTag('event', eventName);
    }
  }, []);

  const setConfig = React.useCallback((value = {}) => {
    callGTag('config', GA_MEASUREMENT_ID, {
      ...(hasDebugMode && { debug_mode: true }),
      ...value,
    });
  }, []);

  const set = React.useCallback((key, value) => {
    callGTag('set', key, value);
  }, []);

  const setUserProperties = React.useCallback(
    (properties) => {
      set('user_properties', properties);
    },
    [set],
  );

  const setUserInfo = React.useCallback(({ userId, ownerOrgId }) => {
    if (userId) {
      setConfig({ user_id: userId });
    }

    // "user_id" is not available in the reports (but is used by GA internally)
    // so "login_id" is used to track individual users in reports that are
    // manually created by Instil.
    setUserProperties({
      ...(userId && { login_id: userId }),
      ...(ownerOrgId && { owner_org_id: ownerOrgId }),
    });
  }, []);

  const linkClickedEvent = React.useCallback((location, target) => {
    sendEvent('link_clicked', {
      location,
      target,
    });
  }, []);

  // When logging out, it is possible for a user to log back in during that
  // same session. To prepare for that, we need to reset the data that this
  // component uses internally.
  const resetAnalyticsState = React.useCallback(() => {
    isDemoOrgRef.current = undefined;
    gTagQueueRef.current = [];
  }, []);

  return (
    <GoogleAnalyticsContext.Provider
      value={{
        linkClickedEvent,
        resetAnalyticsState,
        sendEvent,
        setUserInfo,
      }}
    >
      {children}
    </GoogleAnalyticsContext.Provider>
  );
}

GoogleAnalyticsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
